import { db } from '../src/firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, limit, onSnapshot, where, getDocs, startAfter } from 'firebase/firestore';
import { User } from '../types';

const COLLECTION_NAME = 'user_logs';
const IP_API_URL = 'https://api.ipify.org?format=json';

export interface UserLog {
    id?: string;
    userId: string | null;
    userName: string | null;
    ip: string;
    action: 'VIEW' | 'LOGIN' | 'LOGOUT' | 'ACTION' | 'ERROR';
    details: string;
    path?: string;
    userAgent: string;
    timestamp?: any; // serverTimestamp
}

// Отключено хэширование IP: для админского журнала требуется видеть реальные IP-адреса.

/**
 * Упрощает User-Agent до названия ОС/Устройства (Псевдонимизация)
 */
function getObfuscatedUserAgent(ua: string): string {
    const lowerUA = ua.toLowerCase();
    if (lowerUA.includes('android')) return 'Android';
    if (lowerUA.includes('iphone') || lowerUA.includes('ipad')) return 'iOS';
    if (lowerUA.includes('windows')) return 'Windows';
    if (lowerUA.includes('mac os')) return 'macOS';
    if (lowerUA.includes('linux')) return 'Linux';
    return 'Other';
}

class UserLogsService {
    private ipCache: string | null = null;
    private ipPromise: Promise<string> | null = null;

    /**
     * Получает IP адрес пользователя.
     * Кэширует результат на уровне сессии сервиса (в памяти).
     */
    async getUserIP(): Promise<string> {
        if (this.ipCache) return this.ipCache;

        // Если уже есть активный запрос, возвращаем его
        if (this.ipPromise) return this.ipPromise;

        this.ipPromise = fetch(IP_API_URL)
            .then(res => res.json())
            .then(data => {
                this.ipCache = data.ip || 'unknown';
                this.ipPromise = null;
                return this.ipCache!;
            })
            .catch(err => {
                console.warn('Failed to fetch IP:', err);
                this.ipPromise = null;
                return 'unknown';
            });

        return this.ipPromise;
    }

    /**
     * Записывает лог в Firestore.
     * Не блокирует выполнение (fire and forget), но логирует ошибки в консоль.
     */
    async logActivity(
        action: UserLog['action'],
        details: string,
        user: any | null = null, // Allow both Firebase User and App User
        path: string = window.location.pathname
    ) {
        try {
            // 1. Получаем IP (если еще нет)
            let ip = this.ipCache;
            if (!ip) {
                // Пробуем получить быстро, не блокируя надолго
                try {
                    ip = await Promise.race([
                        this.getUserIP(),
                        new Promise<string>(resolve => setTimeout(() => resolve('unknown-timeout'), 4000))
                    ]);
                } catch (e) {
                    ip = 'unknown-error';
                }
            }

            // 2. Формируем запись (ПСЕВДОНИМИЗАЦИЯ PII)

            // Handle both { id: ... } (App User) and { uid: ... } (Firebase User)
            const userId = user?.uid || user?.id || null;
            // Извлекаем имя или email для идентификации в админ-панели
            const userName = user?.displayName || user?.name || user?.email || null;

            // IP пишется в открытом виде для расследования инцидентов
            const recordIp = ip && ip !== 'unknown-timeout' && ip !== 'unknown-error' ? ip : 'unknown';

            const logEntry: Omit<UserLog, 'id'> = {
                userId,
                userName,
                ip: recordIp,
                action,
                details,
                path,
                userAgent: getObfuscatedUserAgent(navigator.userAgent),
                timestamp: serverTimestamp(),
            };

            // 3. Отправляем в Firestore
            await addDoc(collection(db, COLLECTION_NAME), logEntry);

        } catch (error) {
            // Тихое падение, чтобы не ломать UI
            console.error('Failed to write user log:', error);
        }
    }

    /**
     * Подписка на последние логи (для Админ-панели)
     */
    subscribeToLogs(callback: (logs: UserLog[]) => void, limitCount = 100) {
        const q = query(
            collection(db, COLLECTION_NAME),
            orderBy('timestamp', 'desc'),
            limit(limitCount)
        );

        return onSnapshot(q, (snapshot) => {
            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as UserLog));
            callback(logs);
        }, (error) => {
            console.error("Error subscribing to logs:", error);
        });
    }

    /**
     * Получение логов с фильтрацией (исторические данные).
     * Если фильтры пустые - возвращает последние N записей.
     */
    async getLogs(filters: {
        startDate?: Date | null;
        endDate?: Date | null;
        userId?: string;
        action?: string;
        limit?: number;
        startAfter?: any; // QueryDocumentSnapshot
    } = {}): Promise<{ logs: UserLog[], lastVisible: any }> {
        const constraints: any[] = [orderBy('timestamp', 'desc')];

        if (filters.startDate) {
            constraints.push(where('timestamp', '>=', filters.startDate));
        }

        if (filters.endDate) {
            const end = new Date(filters.endDate);
            end.setHours(23, 59, 59, 999);
            constraints.push(where('timestamp', '<=', end));
        }

        if (filters.userId) {
            constraints.push(where('userId', '==', filters.userId));
        }

        if (filters.action) {
            constraints.push(where('action', '==', filters.action));
        }

        if (filters.startAfter) {
            constraints.push(startAfter(filters.startAfter));
        }

        constraints.push(limit(filters.limit || 100));

        const q = query(collection(db, COLLECTION_NAME), ...constraints);

        try {
            const snapshot = await getDocs(q);
            const lastVisible = snapshot.docs[snapshot.docs.length - 1];
            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as UserLog));

            return { logs, lastVisible };
        } catch (error) {
            console.error("Error fetching logs:", error);
            return { logs: [], lastVisible: null };
        }
    }
}

export const userLogsService = new UserLogsService();
